/**
 * Apartsales — Developer rep onboarding: загрузка планировки.
 *
 * Route: `/onboarding/unit/$unitId/floor-plan`
 *
 * Используется агентом застройщика (через developer_reps) для:
 *   1. Загрузки исходника плана (JPEG/PNG/WebP/PDF)
 *   2. Запуска AI-распознавания комнат
 *   3. Approve / корректировка распознавания → создание unit_rooms
 *
 * Доступ через RLS на уровне БД: загрузка работает только если
 * `is_developer_rep_for(unit.project.developer_id)` или `is_admin()`.
 *
 * Этот route — минимальная точка входа Phase 1 онбординга. Полный
 * CRM (`crm.apartsales.com`) появится в Phase 2 (`apps/crm`).
 */

import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { supabase } from '~/lib/supabase';
import { FloorPlanUploader } from '~/components/onboarding/FloorPlanUploader';
import { FloorPlanGeometryEditor } from '~/components/onboarding/FloorPlanGeometryEditor';

export const Route = createFileRoute('/onboarding/unit/$unitId/floor-plan')({
  component: FloorPlanOnboardingPage,
});

interface UnitRow {
  id: string;
  number: string | null;
  project_id: string | null;
  type: string | null;
  // из VIEW
  project_name: string | null;
  developer_name: string | null;
  floor_plan_url: string | null;
  floor_plan_source: string | null;
  floor_plan_uploaded_at: string | null;
  area_total: number | null;
}

function FloorPlanOnboardingPage() {
  const { unitId } = Route.useParams();
  const [roomsCreatedToast, setRoomsCreatedToast] = useState<number | null>(null);

  const { data: unit, isLoading, error, refetch } = useQuery({
    queryKey: ['onboarding-unit', unitId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('v_unit_full')
        .select(
          [
            'id',
            'number',
            'project_id',
            'type',
            'project_name',
            'developer_name',
            'floor_plan_url',
            'floor_plan_source',
            'floor_plan_uploaded_at',
            'area_total',
          ].join(','),
        )
        .eq('id', unitId)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as UnitRow | null;
    },
  });

  return (
    <section
      className="mx-auto min-h-full max-w-2xl px-4 pb-16 pt-6"
      style={{ paddingTop: 'max(env(safe-area-inset-top), 24px)' }}
    >
      <header className="mb-6">
        <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-black/50">
          Онбординг застройщика
        </p>
        <h1 className="mt-1 text-[24px] font-bold leading-tight text-black">
          Загрузка планировки
        </h1>
        {unit && (
          <p className="mt-1 text-[14px] text-black/65">
            {unit.project_name ?? 'ЖК без названия'}
            {unit.number && ` · юнит ${unit.number}`}
            {unit.developer_name && ` · ${unit.developer_name}`}
          </p>
        )}
      </header>

      {isLoading && <SkeletonState />}

      {error && (
        <ErrorState message={error instanceof Error ? error.message : 'Unknown error'} />
      )}

      {unit && !isLoading && (
        <>
          {/* Текущий статус */}
          {unit.floor_plan_url ? (
            <StatusBar
              source={unit.floor_plan_source}
              uploadedAt={unit.floor_plan_uploaded_at}
            />
          ) : (
            <div className="mb-4 rounded-2xl bg-[var(--color-brand-blue-50)] px-4 py-3 ring-1 ring-[var(--color-brand-blue-100)]">
              <p className="text-[13px] font-semibold text-[var(--color-brand-blue-800)]">
                У этого юнита ещё нет плана
              </p>
              <p className="mt-0.5 text-[12px] text-[var(--color-brand-blue-700)]">
                Загрузите JPEG/PDF от застройщика — AI распознает комнаты, останется проверить и применить.
              </p>
            </div>
          )}

          <FloorPlanUploader
            unitId={unitId}
            existingUrl={unit.floor_plan_url}
            source="manual"
            onUploaded={() => void refetch()}
            onRoomsApplied={(n) => {
              setRoomsCreatedToast(n);
              setTimeout(() => setRoomsCreatedToast(null), 4000);
              void refetch();
            }}
          />

          {/* Звено 3 конвейера — редактор-модерация геометрии */}
          <FloorPlanGeometryEditor
            unitId={unitId}
            imageUrl={unit.floor_plan_url}
            areaTotalM2={unit.area_total}
            onApproved={() => void refetch()}
          />

          <Tips />
        </>
      )}

      {/* Toast о созданных комнатах */}
      {roomsCreatedToast != null && (
        <div
          role="status"
          className="fixed inset-x-4 z-50 mx-auto max-w-md rounded-2xl bg-black/90 px-4 py-3 text-center text-[13px] font-semibold text-white backdrop-blur-md"
          style={{ bottom: 'max(env(safe-area-inset-bottom), 24px)' }}
        >
          Создано {roomsCreatedToast} {pluralize(roomsCreatedToast, ['комната', 'комнаты', 'комнат'])} в разделе «Размеры»
        </div>
      )}
    </section>
  );
}

function StatusBar({
  source,
  uploadedAt,
}: {
  source: string | null;
  uploadedAt: string | null;
}) {
  const srcLabel = source ? sourceLabel(source) : '—';
  const dateLabel = uploadedAt ? new Date(uploadedAt).toLocaleString('ru-RU') : null;
  return (
    <div className="mb-4 flex items-center gap-2 rounded-2xl bg-emerald-50 px-4 py-3 ring-1 ring-emerald-100">
      <CheckIcon />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-semibold text-emerald-900">
          План загружен · источник: {srcLabel}
        </p>
        {dateLabel && (
          <p className="truncate text-[11px] text-emerald-800/80">
            {dateLabel}
          </p>
        )}
      </div>
    </div>
  );
}

function sourceLabel(s: string): string {
  switch (s) {
    case 'manual': return 'ручная загрузка';
    case 'feed': return 'feed застройщика';
    case 'studio': return 'Apartsales Studio';
    case 'migration': return 'миграция';
    case 'partner_api': return 'API адаптер CRM';
    default: return s;
  }
}

function Tips() {
  return (
    <details className="mt-6 rounded-2xl bg-white/[0.04] p-4 ring-1 ring-black/8 open:bg-white/[0.06]">
      <summary className="cursor-pointer list-none text-[13px] font-semibold text-black/85">
        Как получить идеальное распознавание
      </summary>
      <ul className="mt-3 space-y-2 text-[13px] text-black/70">
        <li>• Используйте оригинал из проектной документации (не скриншот мобильного клиента)</li>
        <li>• Если PDF — мы автоматически возьмём первую страницу и отрендерим в 1600 px</li>
        <li>• На плане должны быть подписи комнат на русском и площади в м²</li>
        <li>• Маркетинговая планировка-картинка работает хуже техпаспорта БТИ</li>
        <li>• Если распознано не всё — отклоните, исправьте в карточке вручную</li>
      </ul>
    </details>
  );
}

function SkeletonState() {
  return (
    <div className="space-y-3">
      <div className="h-20 animate-pulse rounded-2xl bg-black/5" />
      <div className="h-72 animate-pulse rounded-3xl bg-black/5" />
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-2xl bg-red-50 px-4 py-3 ring-1 ring-red-200">
      <p className="text-[13px] font-semibold text-red-900">Не удалось загрузить юнит</p>
      <p className="mt-0.5 text-[13px] text-red-800">{message}</p>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
      <circle cx="10" cy="10" r="9" fill="#0E6A2D" />
      <path d="M6 10.5L8.5 13L14 7.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function pluralize(n: number, forms: [string, string, string]): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1];
  return forms[2];
}
